import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { authFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fileUrl } from "@/lib/placeholders";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Minus, PenLine, Type, Shapes, Hand, Undo2, Redo2, Trash2,
  Camera, ImageOff, Printer, Save, FolderInput, Check, ZoomIn, ZoomOut,
  Maximize2, Magnet, Ruler, ImagePlus,
} from "lucide-react";

/**
 * La lavagna del sopralluogo: l'eidotipo del cantiere.
 *
 * È lo schizzo che si fa sul posto col metro in mano. Non è in scala e non
 * vuole esserlo: si tracciano le linee, ci si scrive accanto la misura letta
 * sul metro, si posano i simboli di quello che c'è.
 *
 * Il foglio è più grande dello schermo apposta. Con due dita si stringe, si
 * allarga e si sposta, come su una mappa; sulla destra c'è la barra per
 * scorrere in verticale, per chi è al PC e non ha due dita da appoggiare.
 *
 * Tre cose imparate a spese nostre con la firma a video, e che qui valgono
 * uguale: si usano i Pointer Event (React registra touchstart come passivo,
 * quindi lì preventDefault non ha effetto e la pagina scorre mentre disegni);
 * le coordinate si prendono dal riquadro vero dell'elemento; e il disegno sta
 * in uno spazio logico suo, così quello fatto sul telefono si riapre uguale
 * sul monitor dell'ufficio.
 */
const LARGO = 1000;
const SOGLIA_TOCCO = 26;      // quanto vicino bisogna toccare per prendere qualcosa
// L'aggancio all'orizzontale è spento di partenza e stretto quando è acceso:
// in cantiere si ricalcano le linee della foto, che sono prospettiche, e una
// tolleranza generosa le raddrizza tutte impedendo di seguire il muro.
const GRADI_AGGANCIO = 3;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 8;
const PASSI_STORIA = 60;
// Di quanto la linea di misura si stacca da quella disegnata: abbastanza
// da vedersi che sono due cose diverse, non tanto da non capire più a
// cosa si riferisce. Stesso valore del server, così video e stampa
// disegnano la stessa quota.
const SCOSTAMENTO_QUOTA = 22;

const SPESSORI = [
  { valore: 2, nome: "Sottile" },
  { valore: 4, nome: "Media" },
  { valore: 7, nome: "Grossa" },
];

/** Distanza di un punto da un segmento: serve per capire cosa si sta toccando. */
function distanzaDalSegmento(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lungo = dx * dx + dy * dy;
  let t = lungo ? ((px - x1) * dx + (py - y1) * dy) / lungo : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function distanzaDaOggetto(o, x, y) {
  if (o.tipo === "linea" || o.tipo === "mano" || o.tipo === "quota") {
    let minima = Infinity;
    for (let i = 0; i + 3 < o.punti.length; i += 2) {
      minima = Math.min(minima, distanzaDalSegmento(
        x, y, o.punti[i], o.punti[i + 1], o.punti[i + 2], o.punti[i + 3]));
    }
    return minima;
  }
  if (o.tipo === "simbolo") {
    const raggio = 45 * (o.scala || 1);
    return Math.max(0, Math.hypot(x - o.x, y - o.y) - raggio * 0.6);
  }
  return Math.hypot(x - (o.x + 30), y - (o.y - 6));
}

/** I due estremi di un oggetto: è da lì che nasce la quota. */
function estremi(o) {
  if (o.tipo === "linea" || o.tipo === "mano" || o.tipo === "quota") {
    const p = o.punti;
    return [p[0], p[1], p[p.length - 2], p[p.length - 1]];
  }
  const lato = o.tipo === "simbolo" ? 45 * (o.scala || 1) : 40;
  return [o.x - lato, o.y, o.x + lato, o.y];
}

/** Un simbolo della libreria, disegnato dalle sue primitive.
 *
 * L'inclinazione schiaccia il simbolo sull'asse verticale prima di girarlo:
 * è il modo più semplice di far sembrare che sia appoggiato su un piano che
 * va via in profondità, invece che incollato sopra la foto.
 */
function Simbolo({ simbolo, o, selezionato }) {
  if (!simbolo) return null;
  const lato = 90 * (o.scala || 1);
  const k = lato / 100;
  const schiaccia = Math.cos((Math.min(80, Math.max(0, o.inclina || 0)) * Math.PI) / 180);
  const tratti = simbolo.disegno.map((t, i) => {
    const p = t.p;
    if (t.t === "l") {
      return <line key={i} x1={(p[0] - 50) * k} y1={(p[1] - 50) * k}
                   x2={(p[2] - 50) * k} y2={(p[3] - 50) * k} />;
    }
    if (t.t === "r") {
      return <rect key={i} x={(p[0] - 50) * k} y={(p[1] - 50) * k}
                   width={p[2] * k} height={p[3] * k} />;
    }
    if (t.t === "c") {
      return <circle key={i} cx={(p[0] - 50) * k} cy={(p[1] - 50) * k} r={p[2] * k} />;
    }
    return (
      <text key={i} x={(p[0] - 50) * k} y={(p[1] - 50) * k}
            textAnchor="middle" dominantBaseline="middle"
            fontSize={12 * k} fontWeight="700"
            fill={o.colore} stroke="none">{t.s}</text>
    );
  });
  return (
    <g transform={`translate(${o.x} ${o.y}) rotate(${o.angolo || 0}) scale(1 ${schiaccia})`}
       stroke={o.colore} strokeWidth={2.5} fill="none"
       strokeLinecap="round" strokeLinejoin="round"
       opacity={selezionato ? 0.65 : 1}>
      {tratti}
    </g>
  );
}

/**
 * La quotatura: linea di misura staccata, richiami e frecce.
 *
 * È fatta come su un disegno quotato, e per una ragione pratica: se la linea
 * della quota sta sopra a quella disegnata non si capisce più quale delle due
 * è l'oggetto e quale la misura. Quindi la linea di misura corre parallela e
 * discostata, sottile e tratteggiata, e due richiami sottili la tengono
 * agganciata ai due estremi veri.
 *
 * Il testo si gira sempre dalla parte leggibile: una quota che punta a
 * sinistra, lasciata com'è, avrebbe il numero capovolto.
 */
function Quota({ o, selezionato }) {
  const [ax, ay, bx, by] = o.punti;
  const lungo = Math.hypot(bx - ax, by - ay);
  if (lungo < 1) return null;
  const ux = (bx - ax) / lungo;
  const uy = (by - ay) / lungo;
  const nx = -uy;
  const ny = ux;
  const lato = (o.lato ?? 1) < 0 ? -1 : 1;
  const scosta = (o.scostamento ?? SCOSTAMENTO_QUOTA) * lato;

  const qax = ax + nx * scosta;
  const qay = ay + ny * scosta;
  const qbx = bx + nx * scosta;
  const qby = by + ny * scosta;

  const punta = Math.min(16, lungo * 0.16);
  const apertura = (22 * Math.PI) / 180;
  const sottile = Math.max(0.6, (o.spessore || 2) * 0.45);
  const oltre = 0.18 * Math.abs(scosta);

  const freccia = (qx, qy, verso) => {
    const base = Math.atan2(uy * verso, ux * verso);
    return [1, -1].map((segno) => {
      const a = base + segno * apertura;
      return `M ${qx} ${qy} L ${qx + punta * Math.cos(a)} ${qy + punta * Math.sin(a)}`;
    }).join(" ");
  };

  let gradi = (Math.atan2(qby - qay, qbx - qax) * 180) / Math.PI;
  if (gradi > 90 || gradi < -90) gradi += 180;
  const corpo = 20 * (o.scala || 1);
  const verso = scosta > 0 ? oltre : -oltre;

  return (
    <g opacity={selezionato ? 0.6 : 1} stroke={o.colore}
       strokeWidth={sottile} fill="none" strokeLinecap="round">
      {/* i richiami: dal punto vero fin poco oltre la linea di misura */}
      <line x1={ax + nx * scosta * 0.12} y1={ay + ny * scosta * 0.12}
            x2={qax + nx * verso} y2={qay + ny * verso} />
      <line x1={bx + nx * scosta * 0.12} y1={by + ny * scosta * 0.12}
            x2={qbx + nx * verso} y2={qby + ny * verso} />
      {/* la linea di misura, sottile e tratteggiata */}
      <line x1={qax} y1={qay} x2={qbx} y2={qby} strokeDasharray="7 5" />
      <path d={freccia(qax, qay, 1)} />
      <path d={freccia(qbx, qby, -1)} />
      <g transform={`translate(${(qax + qbx) / 2} ${(qay + qby) / 2}) rotate(${gradi})`}>
        <text x="0" y={-corpo * 0.35} textAnchor="middle"
              fontSize={corpo} fontWeight="700"
              fill="#ffffff" stroke="#ffffff" strokeWidth={6}
              strokeLinejoin="round" paintOrder="stroke">{o.testo}</text>
        <text x="0" y={-corpo * 0.35} textAnchor="middle"
              fontSize={corpo} fontWeight="700"
              fill={o.colore} stroke="none">{o.testo}</text>
      </g>
    </g>
  );
}

export default function Lavagna() {
  const { lavId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const puoDisegnare = ["admin", "amministrativo", "tecnico"].includes(user?.role);

  const [lav, setLav] = useState(null);
  const [attrezzi, setAttrezzi] = useState(null);
  const [oggetti, setOggetti] = useState([]);
  const [storia, setStoria] = useState([]);
  const [rifatti, setRifatti] = useState([]);
  const [strumento, setStrumento] = useState("linea");
  const [colore, setColore] = useState("#e11d48");
  const [spessore, setSpessore] = useState(4);
  const [simbolo, setSimbolo] = useState("contatore");
  const [aggancio, setAggancio] = useState(false);
  const [selezionato, setSelezionato] = useState(null);
  const [inCorso, setInCorso] = useState(null);
  const [scrivendo, setScrivendo] = useState(null);   // {x,y} oppure {quotaSu:[...]}
  const [testo, setTesto] = useState("");
  const [salvato, setSalvato] = useState(true);
  const [pannello, setPannello] = useState(null);     // "colori" | "foto"
  // Dove si e' aperta la tavolozza: si misura il pulsante e ci si mette
  // accanto. Sta in posizione fissa perche' la colonna degli attrezzi
  // scorre, e qualunque cosa dentro una colonna che scorre viene
  // tagliata al bordo.
  const [doveColori, setDoveColori] = useState(null);
  const [vista, setVista] = useState({ x: 0, y: 0, k: 1 });

  const svgRef = useRef(null);
  const coloreRef = useRef(null);
  const scattaRef = useRef(null);
  const allegaRef = useRef(null);
  const trascina = useRef(null);
  const dita = useRef(new Map());
  const gesto = useRef(null);
  const primaVolta = useRef(true);

  const altezza = lav?.altezza || 700;

  const carica = useCallback(async () => {
    try {
      const [{ data: l }, { data: a }] = await Promise.all([
        api.get(`/lavagne/${lavId}`),
        api.get("/lavagne/attrezzi"),
      ]);
      setLav(l);
      setAttrezzi(a);
      setOggetti(l.oggetti || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rilievo non trovato");
      navigate("/cantieri");
    }
  }, [lavId, navigate]);

  useEffect(() => { carica(); }, [carica]);

  const salva = useCallback(async (silenzioso) => {
    if (!lav || !puoDisegnare) return;
    try {
      await api.put(`/lavagne/${lavId}`, { titolo: lav.titolo, altezza, oggetti });
      setSalvato(true);
      if (!silenzioso) toast.success("Rilievo salvato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    }
  }, [lav, lavId, altezza, oggetti, puoDisegnare]);

  // Si salva da solo poco dopo l'ultima modifica: in cantiere ci si dimentica
  // di premere Salva, e uno schizzo perso si rifà solo tornando sul posto.
  useEffect(() => {
    if (primaVolta.current) { primaVolta.current = false; return; }
    setSalvato(false);
    const t = setTimeout(() => salva(true), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oggetti]);

  /** Ogni modifica passa da qui, così Annulla e Ripeti sanno cosa è successo. */
  const cambia = useCallback((fai) => {
    setOggetti((v) => {
      setStoria((s) => [...s, v].slice(-PASSI_STORIA));
      setRifatti([]);
      return fai(v);
    });
  }, []);

  const annulla = () => {
    setStoria((s) => {
      if (!s.length) return s;
      setOggetti((v) => { setRifatti((r) => [...r, v]); return s[s.length - 1]; });
      setSelezionato(null);
      return s.slice(0, -1);
    });
  };

  const ripeti = () => {
    setRifatti((r) => {
      if (!r.length) return r;
      setOggetti((v) => { setStoria((s) => [...s, v]); return r[r.length - 1]; });
      setSelezionato(null);
      return r.slice(0, -1);
    });
  };

  // --- la vista: quanto del foglio si sta guardando --------------------------
  const riquadro = useMemo(() => ({
    x: vista.x, y: vista.y, w: LARGO / vista.k, h: altezza / vista.k,
  }), [vista, altezza]);

  const zooma = (fattore, centro) => {
    setVista((v) => {
      const k = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.k * fattore));
      const c = centro || { x: v.x + LARGO / v.k / 2, y: v.y + altezza / v.k / 2 };
      return { k, x: c.x - (c.x - v.x) * (v.k / k), y: c.y - (c.y - v.y) * (v.k / k) };
    });
  };

  const adatta = () => setVista({ x: 0, y: 0, k: 1 });

  /** Da dove ha toccato il dito allo spazio logico del disegno. */
  const dove = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return {
      x: riquadro.x + ((e.clientX - r.left) / r.width) * riquadro.w,
      y: riquadro.y + ((e.clientY - r.top) / r.height) * riquadro.h,
    };
  };

  const centroDita = () => {
    const p = [...dita.current.values()];
    return {
      x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2,
      d: Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y),
    };
  };

  /** Cosa c'è sotto il dito, se c'è qualcosa. */
  const oggettoSotto = (p) => {
    let scelto = null;
    let minima = SOGLIA_TOCCO / vista.k;
    oggetti.forEach((o, i) => {
      const d = distanzaDaOggetto(o, p.x, p.y);
      if (d < minima) { minima = d; scelto = i; }
    });
    return scelto;
  };

  const giu = (e) => {
    dita.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);

    // Due dita: si guarda il foglio, non si disegna.
    if (dita.current.size === 2) {
      setInCorso(null);
      trascina.current = null;
      const c = centroDita();
      gesto.current = { d: c.d, vista: { ...vista },
                        schermo: { x: c.x, y: c.y }, riquadro: { ...riquadro } };
      return;
    }
    if (dita.current.size > 2 || gesto.current) return;
    if (!puoDisegnare) return;

    const p = dove(e);
    setPannello(null);

    if (strumento === "quota") {
      // Si tocca l'oggetto da quotare: la quota nasce dai suoi due estremi.
      const scelto = oggettoSotto(p);
      if (scelto === null) {
        toast.info("Tocca la linea o l'oggetto da quotare");
        return;
      }
      setSelezionato(null);
      setScrivendo({ quotaSu: estremi(oggetti[scelto]) });
      setTesto("");
      return;
    }
    if (strumento === "sposta") {
      const scelto = oggettoSotto(p);
      setSelezionato(scelto);
      if (scelto !== null) trascina.current = { indice: scelto, da: p, mosso: false };
      return;
    }
    if (strumento === "testo") { setScrivendo({ x: p.x, y: p.y }); setTesto(""); return; }
    if (strumento === "simbolo") {
      cambia((v) => [...v, { tipo: "simbolo", codice: simbolo, x: p.x, y: p.y,
                             colore, scala: 1, angolo: 0, inclina: 0 }]);
      setSelezionato(oggetti.length);
      // Posato il simbolo si passa alla manina: il tocco dopo serve quasi
      // sempre a sistemarlo, non a posarne un altro sopra.
      setStrumento("sposta");
      return;
    }
    setInCorso({ tipo: strumento, punti: [p.x, p.y, p.x, p.y], colore, spessore });
  };

  const muovi = (e) => {
    if (dita.current.has(e.pointerId)) {
      dita.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (gesto.current && dita.current.size === 2) {
      const c = centroDita();
      const g = gesto.current;
      const k = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, g.vista.k * (c.d / (g.d || 1))));
      const r = svgRef.current.getBoundingClientRect();
      const sotto = {
        x: g.riquadro.x + ((g.schermo.x - r.left) / r.width) * g.riquadro.w,
        y: g.riquadro.y + ((g.schermo.y - r.top) / r.height) * g.riquadro.h,
      };
      const frazioneX = (c.x - r.left) / r.width;
      const frazioneY = (c.y - r.top) / r.height;
      setVista({ k, x: sotto.x - frazioneX * (LARGO / k),
                 y: sotto.y - frazioneY * (altezza / k) });
      return;
    }
    if (gesto.current || !puoDisegnare) return;

    const p = dove(e);

    if (trascina.current) {
      const { indice, da } = trascina.current;
      const dx = p.x - da.x;
      const dy = p.y - da.y;
      if (!trascina.current.mosso) {
        trascina.current.mosso = true;
        setStoria((s) => [...s, oggetti].slice(-PASSI_STORIA));
        setRifatti([]);
      }
      trascina.current.da = p;
      setOggetti((v) => v.map((o, i) => {
        if (i !== indice) return o;
        if (o.punti) {
          return { ...o, punti: o.punti.map((n, k) => (k % 2 ? n + dy : n + dx)) };
        }
        return { ...o, x: o.x + dx, y: o.y + dy };
      }));
      return;
    }
    if (!inCorso) return;

    if (inCorso.tipo === "mano") {
      setInCorso((t) => ({ ...t, punti: [...t.punti, p.x, p.y] }));
      return;
    }
    const [x1, y1] = inCorso.punti;
    let { x, y } = p;
    if (aggancio) {
      const gradi = Math.abs(Math.atan2(y - y1, x - x1) * 180 / Math.PI);
      if (gradi < GRADI_AGGANCIO || gradi > 180 - GRADI_AGGANCIO) y = y1;
      else if (Math.abs(gradi - 90) < GRADI_AGGANCIO) x = x1;
    }
    setInCorso((t) => ({ ...t, punti: [x1, y1, x, y] }));
  };

  const su = (e) => {
    dita.current.delete(e.pointerId);
    if (dita.current.size < 2) gesto.current = null;
    trascina.current = null;
    if (!inCorso) return;
    const p = inCorso.punti;
    const lungo = Math.hypot(p[p.length - 2] - p[0], p[p.length - 1] - p[1]);
    if (lungo > 4 / vista.k) { const t = inCorso; cambia((v) => [...v, t]); }
    setInCorso(null);
  };

  const scriviTesto = () => {
    if (!testo.trim() || !scrivendo) { setScrivendo(null); return; }
    const nuovo = scrivendo.quotaSu
      ? { tipo: "quota", punti: scrivendo.quotaSu, testo: testo.trim(),
          colore, spessore: Math.max(2, spessore - 1), scala: 1,
          lato: 1, scostamento: SCOSTAMENTO_QUOTA }
      : { tipo: "testo", x: scrivendo.x, y: scrivendo.y,
          testo: testo.trim(), colore, scala: 1 };
    cambia((v) => [...v, nuovo]);
    setScrivendo(null);
    setTesto("");
  };

  const cambiaSelezionato = (campi) =>
    setOggetti((v) => v.map((o, i) => (i === selezionato ? { ...o, ...campi } : o)));

  const togliSelezionato = () => {
    const i = selezionato;
    cambia((v) => v.filter((_, k) => k !== i));
    setSelezionato(null);
  };

  const caricaFoto = async (file) => {
    setPannello(null);
    if (!file) return;
    const modulo = new FormData();
    modulo.append("file", file);
    try {
      // Le misure le prende il server dalla foto già raddrizzata: quelle che
      // dice il telefono non tengono conto di come è girata davvero.
      const { data } = await api.post(`/lavagne/${lavId}/sfondo`, modulo);
      setLav(data);
      adatta();
      toast.success("Immagine messa sotto il disegno");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const stampa = async () => {
    await salva(true);
    try {
      const r = await authFetch(`/lavagne/${lavId}/pdf`);
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Errore nella stampa"); }
  };

  const neiDocumenti = async () => {
    await salva(true);
    try {
      await api.post(`/lavagne/${lavId}/nei-documenti`);
      toast.success("Messo fra i documenti del cantiere");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const simboli = attrezzi?.simboli || [];
  const perCodice = useMemo(
    () => Object.fromEntries(simboli.map((s) => [s.codice, s])), [simboli]);

  if (!lav || !attrezzi) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">
      Caricamento...
    </div>;
  }

  const sel = selezionato !== null ? oggetti[selezionato] : null;
  // Quanto si può scorrere in verticale: a foglio intero non c'è niente da
  // scorrere, e la barra si spegne da sola.
  const scorribile = Math.max(0, altezza - riquadro.h);

  const Attrezzo = ({ codice, icona: Icona, nome }) => (
    <button type="button" onClick={() => { setStrumento(codice); setSelezionato(null); }}
            data-testid={`attrezzo-${codice}`} title={nome}
            className={`w-full h-11 rounded-lg flex items-center justify-center border ${
              strumento === codice ? "bg-[#0067b2] text-white border-[#0067b2]"
                                   : "bg-white text-slate-600 border-slate-200"}`}>
      <Icona className="w-5 h-5" />
    </button>
  );

  return (
    <div className="h-screen flex flex-col bg-slate-100" data-testid="lavagna">
      <header className="bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-2 flex-wrap flex-shrink-0">
        <Link to={`/cantieri/${lav.site_id}`} data-testid="torna-al-cantiere">
          <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4" /></Button>
        </Link>
        <div className="min-w-0">
          <div className="font-display font-bold truncate">{lav.titolo}</div>
          <div className="text-[11px] text-slate-400">
            Due dita per stringere e spostare · le misure si scrivono a mano
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs flex items-center gap-1 ${
            salvato ? "text-emerald-600" : "text-slate-400"}`}>
            {salvato ? <><Check className="w-3.5 h-3.5" /> salvato</> : "non salvato"}
          </span>
          {puoDisegnare && (
            <Button variant="outline" size="sm" onClick={() => salva(false)}
                    data-testid="salva-lavagna">
              <Save className="w-4 h-4 mr-1" /> Salva
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={stampa} data-testid="stampa-lavagna">
            <Printer className="w-4 h-4" />
          </Button>
          {puoDisegnare && (
            <Button variant="outline" size="sm" onClick={neiDocumenti}
                    title="Metti fra i documenti del cantiere">
              <FolderInput className="w-4 h-4" />
            </Button>
          )}
        </div>
      </header>

      <div className="flex-1 min-h-0 flex">
        {/* Gli attrezzi in colonna a sinistra, sempre sotto il pollice: in
            cantiere si cambia attrezzo di continuo, e una barra che si apre e
            si chiude fa perdere un tocco ogni volta. */}
        {puoDisegnare && (
          <aside className="w-16 flex-shrink-0 bg-white border-r border-slate-200
                            p-2 flex flex-col gap-1.5 overflow-y-auto"
                 data-testid="attrezzi">
            <Attrezzo codice="linea" icona={Minus} nome="Linea dritta" />
            <Attrezzo codice="mano" icona={PenLine} nome="Mano libera" />
            <Attrezzo codice="quota" icona={Ruler} nome="Quota: tocca un oggetto e scrivi la misura" />
            <Attrezzo codice="testo" icona={Type} nome="Nota" />
            <Attrezzo codice="simbolo" icona={Shapes} nome="Simbolo" />
            <Attrezzo codice="sposta" icona={Hand} nome="Sposta e seleziona" />

            <div className="h-px bg-slate-200 my-1" />

            <button type="button" data-testid="scegli-colore" ref={coloreRef}
                    onClick={() => {
                      if (pannello === "colori") { setPannello(null); return; }
                      const r = coloreRef.current?.getBoundingClientRect();
                      if (r) setDoveColori({ top: r.top, left: r.right + 8 });
                      setPannello("colori");
                    }}
                    className="w-full h-11 rounded-lg border border-slate-200 bg-white flex items-center justify-center">
              <span className="w-6 h-6 rounded-full border border-slate-300"
                    style={{ background: colore }} />
            </button>
            {SPESSORI.map((s) => (
              <button key={s.valore} type="button" onClick={() => setSpessore(s.valore)}
                      title={s.nome}
                      className={`w-full h-9 rounded-lg border flex items-center justify-center ${
                        spessore === s.valore ? "border-[#0067b2] bg-blue-50" : "border-slate-200 bg-white"}`}>
                <span className="rounded-full bg-slate-700"
                      style={{ width: s.valore * 2, height: s.valore * 2 }} />
              </button>
            ))}
            <button type="button" onClick={() => setAggancio((a) => !a)}
                    data-testid="aggancio"
                    title={aggancio
                      ? "Le linee si raddrizzano quando sono quasi dritte"
                      : "Le linee restano come le tracci: serve per ricalcare la prospettiva"}
                    className={`w-full h-11 rounded-lg border flex items-center justify-center ${
                      aggancio ? "bg-[#0067b2] text-white border-[#0067b2]"
                               : "bg-white text-slate-400 border-slate-200"}`}>
              <Magnet className="w-5 h-5" />
            </button>

            <div className="h-px bg-slate-200 my-1" />

            <button type="button" onClick={annulla} disabled={!storia.length}
                    data-testid="annulla-ultimo" title="Annulla"
                    className="w-full h-11 rounded-lg border border-slate-200 bg-white
                               flex items-center justify-center text-slate-600 disabled:opacity-30">
              <Undo2 className="w-5 h-5" />
            </button>
            <button type="button" onClick={ripeti} disabled={!rifatti.length}
                    data-testid="ripeti" title="Ripeti"
                    className="w-full h-11 rounded-lg border border-slate-200 bg-white
                               flex items-center justify-center text-slate-600 disabled:opacity-30">
              <Redo2 className="w-5 h-5" />
            </button>

            <div className="h-px bg-slate-200 my-1" />

            <input ref={scattaRef} type="file" accept="image/*" capture="environment"
                   hidden onChange={(e) => caricaFoto(e.target.files?.[0])} />
            <input ref={allegaRef} type="file" accept="image/*" hidden
                   onChange={(e) => caricaFoto(e.target.files?.[0])} />
            <button type="button" data-testid="foto-sfondo" title="Immagine di sfondo"
                    onClick={() => setPannello(p => p === "foto" ? null : "foto")}
                    className="w-full h-11 rounded-lg border border-slate-200 bg-white
                               flex items-center justify-center text-slate-600">
              <Camera className="w-5 h-5" />
            </button>
            {lav.sfondo && (
              <button type="button" title="Togli l'immagine"
                      onClick={async () => {
                        const { data } = await api.delete(`/lavagne/${lavId}/sfondo`);
                        setLav(data); adatta();
                      }}
                      className="w-full h-11 rounded-lg border border-slate-200 bg-white
                                 flex items-center justify-center text-slate-600">
                <ImageOff className="w-5 h-5" />
              </button>
            )}

            <div className="h-px bg-slate-200 my-1" />

            <button type="button" onClick={() => zooma(1.35)} data-testid="zoom-piu"
                    className="w-full h-10 rounded-lg border border-slate-200 bg-white
                               flex items-center justify-center text-slate-600">
              <ZoomIn className="w-5 h-5" />
            </button>
            <div className="text-[10px] tabular-nums text-slate-500 text-center">
              {Math.round(vista.k * 100)}%
            </div>
            <button type="button" onClick={() => zooma(1 / 1.35)} data-testid="zoom-meno"
                    className="w-full h-10 rounded-lg border border-slate-200 bg-white
                               flex items-center justify-center text-slate-600">
              <ZoomOut className="w-5 h-5" />
            </button>
            <button type="button" onClick={adatta} data-testid="adatta"
                    title="Rimetti tutto il foglio nello schermo"
                    className="w-full h-10 rounded-lg border border-slate-200 bg-white
                               flex items-center justify-center text-slate-600">
              <Maximize2 className="w-5 h-5" />
            </button>
          </aside>
        )}

        <main className="flex-1 min-w-0 p-2 sm:p-3 overflow-hidden flex flex-col">
          {/* Sotto il disegno ci va sia la foto scattata sul posto sia
              un'immagine già presa altrove — una vista dall'alto, una
              panoramica, il ritaglio di una tavola. */}
          {pannello === "foto" && (
            <div className="mb-2 bg-white border border-slate-200 rounded-lg p-2 flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" data-testid="scatta-foto"
                      onClick={() => scattaRef.current?.click()}>
                <Camera className="w-4 h-4 mr-2" /> Scatta una foto
              </Button>
              <Button variant="outline" size="sm" data-testid="allega-foto"
                      onClick={() => allegaRef.current?.click()}>
                <ImagePlus className="w-4 h-4 mr-2" /> Allega un&apos;immagine
              </Button>
              <span className="text-[11px] text-slate-400 self-center">
                una vista dall&apos;alto, una panoramica, il ritaglio di una tavola
              </span>
            </div>
          )}

          {strumento === "simbolo" && (
            <div className="mb-2 bg-white border border-slate-200 rounded-lg p-2 flex gap-2 overflow-x-auto flex-shrink-0">
              {simboli.map((s) => (
                <button key={s.codice} type="button" onClick={() => setSimbolo(s.codice)}
                        data-testid={`simbolo-${s.codice}`}
                        className={`flex-shrink-0 w-16 rounded-lg border p-1 ${
                          simbolo === s.codice ? "border-[#0067b2] bg-blue-50" : "border-slate-200"}`}>
                  <svg viewBox="-50 -50 100 100" className="w-full h-10">
                    <Simbolo simbolo={s} o={{ x: 0, y: 0, scala: 1, colore: "#334155" }} />
                  </svg>
                  <span className="block text-[9px] leading-tight text-slate-500 truncate">
                    {s.nome}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="flex-1 min-h-0 flex gap-2">
            <div className="flex-1 min-w-0 flex items-start justify-center overflow-hidden">
              <svg ref={svgRef}
                   viewBox={`${riquadro.x} ${riquadro.y} ${riquadro.w} ${riquadro.h}`}
                   data-testid="foglio"
                   onPointerDown={giu} onPointerMove={muovi}
                   onPointerUp={su} onPointerCancel={su}
                   className="bg-white shadow-lg rounded max-w-full max-h-full"
                   style={{ width: "100%", touchAction: "none",
                            aspectRatio: `${LARGO} / ${altezza}` }}>
                {lav.sfondo && (
                  <image href={fileUrl(lav.sfondo.file_url)} x="0" y="0"
                         width={LARGO} height={altezza} preserveAspectRatio="none" />
                )}
                <rect x="0" y="0" width={LARGO} height={altezza} fill="none"
                      stroke="#cbd5e1" strokeWidth={1 / vista.k} />

                {oggetti.map((o, i) => {
                  const scelto = i === selezionato;
                  if (o.tipo === "quota") {
                    return <Quota key={i} o={o} selezionato={scelto} />;
                  }
                  if (o.tipo === "linea" || o.tipo === "mano") {
                    return (
                      <polyline key={i}
                                points={o.punti.reduce((a, n, k) => (
                                  k % 2 ? `${a}${n} ` : `${a}${n},`), "")}
                                fill="none" stroke={o.colore} strokeWidth={o.spessore}
                                strokeLinecap="round" strokeLinejoin="round"
                                opacity={scelto ? 0.6 : 1} />
                    );
                  }
                  if (o.tipo === "testo") {
                    return (
                      <g key={i} opacity={scelto ? 0.6 : 1}>
                        <text x={o.x} y={o.y} fontSize={22 * (o.scala || 1)}
                              fontWeight="700" fill="#ffffff" stroke="#ffffff"
                              strokeWidth={6} strokeLinejoin="round"
                              paintOrder="stroke">{o.testo}</text>
                        <text x={o.x} y={o.y} fontSize={22 * (o.scala || 1)}
                              fontWeight="700" fill={o.colore}>{o.testo}</text>
                      </g>
                    );
                  }
                  return <Simbolo key={i} simbolo={perCodice[o.codice]} o={o}
                                  selezionato={scelto} />;
                })}

                {inCorso && (
                  <polyline points={inCorso.punti.reduce((a, n, k) => (
                              k % 2 ? `${a}${n} ` : `${a}${n},`), "")}
                            fill="none" stroke={inCorso.colore}
                            strokeWidth={inCorso.spessore} strokeLinecap="round" />
                )}
              </svg>
            </div>

            {/* La barra di scorrimento verticale, a destra del foglio. Con
                due dita si sposta comunque; questa serve al PC e quando si è
                molto ingranditi e il pollice è impegnato a disegnare. */}
            <div className="w-8 flex-shrink-0 flex flex-col items-center py-1">
              <input type="range" min="0" max={Math.max(1, Math.round(scorribile))}
                     step="1" value={Math.round(Math.min(vista.y, scorribile))}
                     disabled={scorribile < 1} data-testid="scorri-verticale"
                     onChange={(e) => setVista((v) => ({ ...v, y: Number(e.target.value) }))}
                     className="h-full cursor-pointer disabled:opacity-25"
                     style={{ writingMode: "vertical-lr", width: "10px" }} />
            </div>
          </div>
        </main>
      </div>

      {/* La tavolozza si apre accanto al pulsante del colore, che mostra
          l'ultimo usato: si scende con il dito e si prende quello nuovo
          senza attraversare mezzo schermo. */}
      {pannello === "colori" && doveColori && (
        <div className="fixed z-30 bg-white border border-slate-200 rounded-lg shadow-xl p-2
                        grid grid-cols-4 gap-2"
             data-testid="tavolozza"
             style={{ top: doveColori.top, left: doveColori.left }}>
          {attrezzi.colori.map((c) => (
            <button key={c.codice} type="button" title={c.nome}
                    data-testid={`colore-${c.codice}`}
                    onClick={() => {
                      setColore(c.codice);
                      if (sel) cambiaSelezionato({ colore: c.codice });
                      setPannello(null);
                    }}
                    className={`w-9 h-9 rounded-full border-2 ${
                      colore === c.codice ? "border-slate-800" : "border-slate-200"}`}
                    style={{ background: c.codice }} />
          ))}
        </div>
      )}

      {/* Quello che si sta scrivendo: una barra in basso, non una finestra.
          Con la tastiera aperta sul telefono una finestra si mangia lo
          schermo e non si vede più dove si sta scrivendo. */}
      {scrivendo && (
        <div className="bg-white border-t border-slate-200 p-3 flex items-center gap-2 flex-shrink-0">
          <Input autoFocus value={testo} data-testid="testo-lavagna"
                 placeholder={scrivendo.quotaSu
                   ? "La misura di questo tratto, letta sul metro"
                   : "Una nota da scrivere sul disegno"}
                 onChange={(e) => setTesto(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && scriviTesto()} />
          <Button onClick={scriviTesto} disabled={!testo.trim()}
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {scrivendo.quotaSu ? "Quota" : "Scrivi"}
          </Button>
          <Button variant="outline" onClick={() => setScrivendo(null)}>Lascia</Button>
        </div>
      )}

      {sel && !scrivendo && (
        <div className="bg-white border-t border-slate-200 p-3 flex items-center gap-4 flex-wrap flex-shrink-0"
             data-testid="pannello-selezione">
          <span className="text-xs uppercase tracking-widest text-slate-400">
            {sel.tipo === "simbolo" ? (perCodice[sel.codice]?.nome || "Simbolo")
              : sel.tipo === "testo" ? "Scritta"
              : sel.tipo === "quota" ? "Quota" : "Linea"}
          </span>
          {(sel.tipo === "simbolo" || sel.tipo === "testo" || sel.tipo === "quota") && (
            <label className="flex items-center gap-2 text-xs text-slate-600">
              grandezza
              <input type="range" min="0.3" max="4" step="0.05"
                     value={sel.scala ?? 1} data-testid="scala-selezione"
                     onChange={(e) => cambiaSelezionato({ scala: Number(e.target.value) })} />
              <span className="tabular-nums w-8">{Math.round((sel.scala ?? 1) * 100)}%</span>
            </label>
          )}
          {sel.tipo === "quota" && (<>
            <label className="flex items-center gap-2 text-xs text-slate-600"
                   title="Quanto la linea della misura si stacca dal disegno">
              distacco
              <input type="range" min="6" max="70" step="1"
                     value={sel.scostamento ?? SCOSTAMENTO_QUOTA}
                     data-testid="scostamento-quota"
                     onChange={(e) => cambiaSelezionato({ scostamento: Number(e.target.value) })} />
            </label>
            <Button variant="outline" size="sm" data-testid="gira-quota"
                    onClick={() => cambiaSelezionato({ lato: (sel.lato ?? 1) * -1 })}>
              Dall&apos;altra parte
            </Button>
          </>)}
          {sel.tipo === "simbolo" && (<>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              rotazione
              <input type="range" min="0" max="359" step="1"
                     value={sel.angolo ?? 0} data-testid="angolo-selezione"
                     onChange={(e) => cambiaSelezionato({ angolo: Number(e.target.value) })} />
              <span className="tabular-nums w-8">{Math.round(sel.angolo ?? 0)}°</span>
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600"
                   title="Corica il simbolo all'indietro, per appoggiarlo sulla prospettiva della foto">
              profondità
              <input type="range" min="0" max="80" step="1"
                     value={sel.inclina ?? 0} data-testid="inclina-selezione"
                     onChange={(e) => cambiaSelezionato({ inclina: Number(e.target.value) })} />
              <span className="tabular-nums w-8">{Math.round(sel.inclina ?? 0)}°</span>
            </label>
          </>)}
          <Button variant="outline" size="sm" onClick={togliSelezionato}
                  data-testid="togli-selezione"
                  className="ml-auto text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1" /> Togli
          </Button>
        </div>
      )}
    </div>
  );
}
